import { useEffect, useEffectEvent } from "react";

import { isWebPushPlatformEligible } from "./webPushEligibility";

type UseBrowserWebPushOptOutSyncProps = {
  isUserLoggedIn: boolean;
  onFail: () => void;
  syncBrowserOptOut: () => Promise<void>;
};

export const useBrowserWebPushOptOutSync = ({
  isUserLoggedIn,
  onFail,
  syncBrowserOptOut,
}: UseBrowserWebPushOptOutSyncProps) => {
  const handleSyncFailure = useEffectEvent(onFail);
  const syncOptOut = useEffectEvent(syncBrowserOptOut);

  useEffect(() => {
    let isBrowserOptOutSynced = false;
    let isBrowserOptOutSyncInFlight = false;

    const reconcileBrowserOptOut = () => {
      const shouldSyncBrowserOptOut =
        !isBrowserOptOutSynced &&
        !isBrowserOptOutSyncInFlight &&
        isUserLoggedIn &&
        isWebPushPlatformEligible() &&
        Notification.permission === "denied";

      if (!shouldSyncBrowserOptOut) return;

      isBrowserOptOutSyncInFlight = true;
      void syncOptOut()
        .then(() => {
          isBrowserOptOutSynced = true;
        })
        .catch(handleSyncFailure)
        .finally(() => {
          isBrowserOptOutSyncInFlight = false;
        });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") reconcileBrowserOptOut();
    };

    reconcileBrowserOptOut();
    window.addEventListener("focus", reconcileBrowserOptOut);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("focus", reconcileBrowserOptOut);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
    // TODO eslint-plugin-react-hooks@5.2.0 does not recognize useEffectEvent; remove after upgrading to >=6.1.1.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserLoggedIn]);
};
